import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Lightbulb, RefreshCw } from 'lucide-react';
import { PacReplyView } from './PacReplyChips';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Phase 4 (new feature) — one AI coaching tip per team per day. Renders the
// same PacReplyView chips as chat/scouting-report when `structured` is
// present (Phase 2/4 flags on); otherwise shows the plain tip text.
export const TeamTipCard = ({ user }) => {
  const [tip, setTip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchTip = async (forceRefresh = false) => {
    if (!user?.team_id) return;
    forceRefresh ? setRefreshing(true) : setLoading(true);
    try {
      const res = await axios.get(`${API}/ai/team-tips/${user.team_id}`, {
        params: {
          user_id: user.id || user.user_id || 'coach',
          coach_name: user.name || user.first_name || 'Coach',
          force_refresh: forceRefresh,
        },
      });
      setTip(res.data);
    } catch (e) {
      // Non-critical widget — fail silently, card just won't render content.
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchTip(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.team_id]);

  if (!user?.team_id) return null;

  return (
    <Card className="border-2 border-accent/30 bg-gradient-to-r from-accent/5 to-primary/5" data-testid="team-tip-card">
      <CardHeader className="border-b bg-accent/10 flex flex-row items-center justify-between">
        <div className="flex items-center gap-3">
          <Lightbulb className="h-6 w-6 text-accent" />
          <CardTitle className="font-heading uppercase text-xl">Coaching Tip of the Day</CardTitle>
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={refreshing || loading}
          onClick={() => fetchTip(true)}
          data-testid="team-tip-refresh-button"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>
      <CardContent className="p-6">
        {loading ? (
          <p className="text-sm text-muted-foreground" data-testid="team-tip-loading">Loading today's tip...</p>
        ) : !tip ? (
          <p className="text-sm text-muted-foreground" data-testid="team-tip-empty">No tip available right now.</p>
        ) : tip.structured ? (
          <PacReplyView structured={tip.structured} />
        ) : (
          <p className="text-sm whitespace-pre-wrap" data-testid="team-tip-text">{tip.tip}</p>
        )}
      </CardContent>
    </Card>
  );
};

export default TeamTipCard;
